/**
 * How a rating is written down for a reader. Always two decimals.
 *
 * The numbers arrive from Postgres NUMERIC as whatever the value happens to be
 * — 2.33 keeps its decimals and 2.00 arrives as 2 — so a squad table came out
 * reading 3.17, 2, 2.63, 3 down the column. Every one of those is correct and
 * the column still looks broken, because a rating is a MEASUREMENT to two
 * places and a number that drops them reads as a different kind of thing
 * altogether. Ratings are also compared by eye constantly — a captain checking
 * a pair against the 6.15 cap — and ragged decimals make that harder than it
 * needs to be.
 *
 * One function rather than `.toFixed(2)` at each site, because the sites that
 * already did that were never the problem: it is the ones that did not, and a
 * shared function is the only thing a test can check for.
 */

/** The dash for a player the season never rated. Not a nought — see below. */
export const NO_RATING = '–';

/**
 * A rating as text, to two decimals.
 *
 * Null and undefined give the dash rather than 0.00, and the distinction is the
 * point: a substitute the league never rated has no rating, which is not the
 * same as the lowest one. Season 1 has six such players and its own site showed
 * them as a dash too.
 */
export function ratingText(value: number | null | undefined, absent = NO_RATING): string {
  return value === null || value === undefined || Number.isNaN(value) ? absent : value.toFixed(2);
}

/**
 * A rating MOVEMENT, with its sign written out: "+0.12", "−0.07".
 *
 * A proper minus sign (U+2212) rather than a hyphen, because these sit in a
 * column beside scorelines full of hyphens and a movement that reads as
 * punctuation is a movement nobody notices.
 *
 * Shared rather than written at each site. It began as a local helper inside
 * the match ledger, and the profile page then wrote its own `.toFixed(2)`
 * version a few months later — the exact drift `ratingText` exists to stop,
 * caught only because the lint in this module's test swept it up. A second
 * copy of a formatter is a second answer to "how is this written down".
 */
export function signedRating(value: number): string {
  return `${value >= 0 ? '+' : '\u2212'}${ratingText(Math.abs(value))}`;
}

/**
 * The odds on a pairing that has not been played, as a pair of whole percents.
 *
 * "41 · 59", home first, to sit between two names on a fixture card. Not a
 * sentence: this goes on a row an eye scans, and the sentence explaining what
 * the ratings meant belongs in the ledger after the match.
 *
 * ROUNDED TO SUM TO A HUNDRED. Two independent roundings of 0.415 and 0.585
 * give 42 and 59, which adds to 101 and looks like a mistake to anybody who
 * checks — and on a card about odds, somebody will. The away figure is taken
 * as the remainder rather than rounded on its own.
 *
 * Null in, null out: a pairing half named, or one with a player the season
 * holds no rating for, gets no prediction rather than a made-up one.
 */
export function predictionSplit(homeShare: number | null | undefined): {
  home: number;
  away: number;
} | null {
  if (homeShare === null || homeShare === undefined || !Number.isFinite(homeShare)) return null;
  const home = Math.round(Math.min(1, Math.max(0, homeShare)) * 100);
  return { home, away: 100 - home };
}
